import { decodeUserOpCalls } from '../../lib/userop-calls'
import type { SigningPayload } from '../../lib/wire-userop'
import type { Transaction } from './types'

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

export function isMinimalSafeOperation(transaction: Transaction): boolean {
  if (transaction.decoded && transaction.decoded.kind !== 'deploy') {
    return false
  }
  const to = transaction.to?.toLowerCase()
  const value = transaction.value?.toString() || '0'
  const data = transaction.data?.toLowerCase() || '0x'
  return to === ZERO_ADDRESS && (value === '0' || value === '0x0') && (data === '0x' || data === '0x0')
}

export function isUserOpSigningPayload(signing: unknown): signing is SigningPayload {
  if (signing === null || typeof signing !== 'object') return false
  const candidate = signing as Record<string, unknown>
  return (
    candidate.kind === 'userOp' &&
    typeof candidate.chainId === 'number' &&
    Number.isInteger(candidate.chainId) &&
    candidate.chainId > 0 &&
    typeof candidate.entryPoint === 'string' &&
    /^0x[0-9a-fA-F]{40}$/.test(candidate.entryPoint) &&
    candidate.userOp !== null &&
    typeof candidate.userOp === 'object' &&
    typeof (candidate.userOp as Record<string, unknown>).callData === 'string' &&
    typeof (candidate.userOp as Record<string, unknown>).sender === 'string'
  )
}

export function effectiveTransaction(transaction: Transaction): Transaction {
  if (!transaction.signing) {
    return transaction
  }
  if (!isUserOpSigningPayload(transaction.signing)) {
    throw new Error('Unsupported signing payload for a transaction')
  }
  const { userOp } = transaction.signing
  const decoded = decodeUserOpCalls(userOp)
  const details = {
    ...transaction.userOpDetails,
    sender: userOp.sender,
    callGasLimit: userOp.callGasLimit,
    verificationGasLimit: userOp.verificationGasLimit,
    preVerificationGas: userOp.preVerificationGas,
    maxFeePerGas: userOp.maxFeePerGas,
    maxPriorityFeePerGas: userOp.maxPriorityFeePerGas,
    paymaster: userOp.paymaster,
    factory: userOp.factory,
    factoryData: userOp.factoryData,
    nonce: userOp.nonce
  }

  if (decoded.kind === 'deploy') {
    return {
      ...transaction,
      to: ZERO_ADDRESS,
      value: '0',
      data: '0x',
      decoded,
      userOpDetails: { ...details, callTarget: ZERO_ADDRESS, value: '0', callData: '0x', operations: undefined }
    }
  }

  if (decoded.kind === 'unknown') {
    return {
      ...transaction,
      to: '',
      value: '0',
      data: userOp.callData,
      decoded,
      userOpDetails: { ...details, callTarget: '', value: '0', callData: userOp.callData, operations: undefined }
    }
  }

  const [first] = decoded.calls
  const total = decoded.calls.reduce((sum, call) => sum + BigInt(call.value), 0n).toString()
  const isBatch = decoded.calls.length > 1
  return {
    ...transaction,
    to: first.to,
    value: total,
    data: first.data,
    decoded,
    userOpDetails: {
      ...details,
      callTarget: first.to,
      value: total,
      callData: first.data,
      operations: isBatch ? decoded.calls.map((call) => ({ to: call.to, value: call.value, data: call.data })) : undefined
    }
  }
}
